import React, { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import RoleSelectionCompetencyPreview from 'components/MeshList/levelUp/RoleSelectionSidePanel/RoleSelectionCompetencyPreview';
import Select from 'react-select';
import { FunctionSelection } from 'screens/admin/Competencies/CompetencieByFunctionAndRole/CompetencyByFunctionAndRole';
import { GenericSidePanelHeader } from 'screens/common/sidepanel/TasksGoalsSidepanelHeader';
import styled from 'styled-components';
import { Button, StyledDrawer } from 'styles/admin';
import { StyledBox, StyledGrid, VerticallyAlignedFlexContainer } from 'styles/common';

import { LevelUpIcon } from 'constants/icons';
import { sendSetupLevelUpRoleEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import theme from 'constants/theme';

import { getOrgFunctionsAndRolesList } from 'services/growth/oneOnOne';

import { Typography12, Typography13, Typography6 } from 'uiToolkit/Typography';

import { useLevelUpPageContext } from '../../context';

const ModalHeader = ({ handleClose }) => (
  <StyledGrid container justify="space-between" alignItems="flex-start" style={{ padding: '16px 24px' }}>
    <Typography13 color={theme.palette.text.heading} style={{ fontSize: '14px', lineHeight: '24px' }}>
      Please select new role you want to work towards
    </Typography13>
  </StyledGrid>
);

const SelectFunction = ({ options, selectedFunction, handleFunctionChange }) => (
  <div>
    <Typography12 color={theme.palette.text.label} bottomGutter={1}>
      Function
    </Typography12>
    {/* 
        Following is poor way of reusing code. 
        The component below is trying to handle a lot of use cases.
        It should have been avoided but that's not the case. (again)
      */}
    <FunctionSelection
      setSelectedFunction={(value) => {
        const selectedOption = options.find(({ uuid }) => String(uuid) === String(value));
        handleFunctionChange(selectedOption);
      }}
      selectedFunction={String(selectedFunction?.uuid)}
      isDropdownOnly
      placeholder="Select Role to be pursued"
      isRowClickable
    />
  </div>
);

const SelectRole = ({ options, selectedRole, handleRoleChange }) => (
  <div>
    <Typography12 color={theme.palette.text.label} bottomGutter={1}>
      Role
    </Typography12>
    <Select
      placeholder="Select Role"
      options={options}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.uuid}
      onChange={handleRoleChange}
      menuPlacement="auto"
      value={selectedRole}
    />
  </div>
);

const ModalFooter = ({ isSubmitDisabled, onSubmit, onCancel, updatingRole }) => (
  <FooterWrapper>
    <StyledGrid container justify="flex-end" alignItems="center" topGutter={3}>
      <Typography13 onClick={onCancel} style={{ marginRight: 'auto' }} weight={600} disabled={false}>
        Cancel
      </Typography13>
      <Button
        size="large"
        blue
        style={{ width: '6.25rem', padding: '0.5rem', fontWeight: '600' }}
        disabled={isSubmitDisabled || updatingRole}
        onClick={onSubmit}
      >
        Save & Close
      </Button>
    </StyledGrid>
  </FooterWrapper>
);

const FooterWrapper = styled.div`
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const ContentAndActionsContainer = styled.div`
  padding: 0 24px;
  flex: 1 1 auto;
  overflow-y: auto;
`;

export const LevelUpRoleSelectionSidePanel = () => {
  const {
    state: { isRoleSelectionModalOpen, levelUpDetails, isNewRoleSelection },
    closeRoleSelectionModal,
    handleRoleSelect,
  } = useLevelUpPageContext();

  const queryClient = useQueryClient();

  const { currentJobFunction, currentJobRole, targetJobFunction, targetJobRole } = levelUpDetails ?? {};

  const [allFunctions, setAllFunctions] = useState([]);
  const [functionRoles, setFunctionRoles] = useState([]);

  const [selectedFunction, setSelectedFunction] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  const [updatingRole, setUpdatingRole] = useState(false);

  const handleFunctionChange = (value) => {
    setSelectedFunction(value);
    setSelectedRole(null);
  };

  const handleRoleChange = (value) => {
    setSelectedRole(value);
  };

  const handleSubmit = async () => {
    // handle submit here.
    try {
      if (targetJobFunction?.uuid !== selectedFunction.uuid || targetJobRole?.uuid !== selectedRole.uuid) {
        setUpdatingRole(true);
        const res = await handleRoleSelect({
          jobFunctionId: selectedFunction.uuid,
          jobRoleId: selectedRole.uuid,
        });
        sendSetupLevelUpRoleEvent({
          eventProperties: {
            target: isNewRoleSelection ? 'New Role' : 'Current Role',
            function:
              currentJobFunction.uuid === selectedFunction.uuid
                ? 'Same Function as current role'
                : 'Different Function from current role',
          },
        });
      }

      queryClient.invalidateQueries([REACT_QUERY_KEYS.LevelUpCompetencyData]);

      closeRoleSelectionModal();
    } catch (error) {
      console.error('Error updating the role : ', error);
    } finally {
      setUpdatingRole(false);
    }
  };

  useEffect(() => {
    getOrgFunctionsAndRolesList()
      .then((functionList) => {
        // the functions with no roles are not useful here. filtering them out.
        const functionsHavingRoles = functionList;
        setAllFunctions(functionsHavingRoles);
      })
      .catch((err) => console.error(err.message || 'some error occurred while fetching function list'));
  }, []);

  useEffect(() => {
    if (selectedFunction) {
      const matchedFunction = allFunctions.find(({ uuid }) => uuid === selectedFunction.uuid);
      setFunctionRoles(matchedFunction.roles);
    } else {
      setFunctionRoles([]);
    }
  }, [selectedFunction, currentJobFunction]);

  useEffect(() => {
    if (currentJobFunction) {
      const matchedFunction = allFunctions.find(({ uuid }) => uuid === currentJobFunction?.uuid);
      setSelectedFunction(matchedFunction);
      const matchedRole = matchedFunction?.roles?.find(({ uuid }) => uuid === currentJobRole?.uuid);
      setSelectedRole(matchedRole);
    }
  }, [currentJobFunction, currentJobRole, allFunctions]);

  return (
    <Drawer anchor="right" open={isRoleSelectionModalOpen} onClose={closeRoleSelectionModal}>
      <Container>
        <LevelUpSidePanelHeader
          closePanel={closeRoleSelectionModal}
          leftChild={
            <VerticallyAlignedFlexContainer>
              <LevelUpIcon style={{ marginRight: '1rem' }} color={theme.palette.icons.blue} size="fixed16px" />
              <Typography6 weight={600}>Develop</Typography6>
            </VerticallyAlignedFlexContainer>
          }
        />

        <ModalHeader />
        <ContentAndActionsContainer>
          <StyledBox bottomGutter={3}>
            <SelectFunction
              options={allFunctions}
              selectedFunction={selectedFunction}
              handleFunctionChange={handleFunctionChange}
            />
          </StyledBox>
          <StyledBox bottomGutter={3}>
            <SelectRole options={functionRoles} selectedRole={selectedRole} handleRoleChange={handleRoleChange} />
          </StyledBox>
          <RoleSelectionCompetencyPreview roleId={selectedRole?.uuid} functionId={selectedFunction?.uuid} />
        </ContentAndActionsContainer>
        <ModalFooter
          isSubmitDisabled={selectedRole === null}
          onSubmit={handleSubmit}
          onCancel={closeRoleSelectionModal}
          updatingRole={updatingRole}
        />
      </Container>
    </Drawer>
  );
};

const LevelUpSidePanelHeader = styled(GenericSidePanelHeader)`
  flex: 0 0 auto;
  padding: 16px;
  border-bottom: 1px solid ${(props) => props.theme.palette.borders.lighterGrey};
`;

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
`;

const Drawer = styled(StyledDrawer)`
  ${(props) => props.theme.breakpoints.up('sm')} {
    .MuiDrawer-paper {
      width: 40%;
      padding: 0;
    }
  }
`;
